import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { GroupStatus, objectGroupsActions, useDispatchObjectGroups, useObjectGroups } from "contexts/objectGroups";
import { SelectionSet } from "utils/selectionSet";

import { ObjectVisibility } from "..";
import { renderActions, selectAutofreezeEnabled, selectDefaultVisibility } from "../renderSlice";

const GROUP_FREEZING_DELAY = 10000;
const GROUP_QUEUE_MERGE_INTERVAL = 3000;
const DEFAULT_ACTION_FREEZING_DELAY = 10000;
const HIDDEN_FREEZING_DELAY = 10000;
const HIDDEN_QUEUE_MERGE_INTERVAL = 3000;

/**
 * Problem: users hide a lot of objects and want to focus on the visible ones,
 * but hidden objects are still taking up memory/primitive budget,
 * so visible objects don't reach desired level of detail.
 *
 * Users should use frozen groups, but that's more work.
 * And sometimes they don't want to use frozen groups - just do some quick selection and hide
 * or set visibility to show only selected.
 *
 * Solution: if an object is hidden long enough (e.g. 10 seconds) - freeze it.
 * Assumption is that the longer the object is hidden - the less likely it is to be needed in the near future.
 * Imagine scenario when a user toggles groups on and off quickly - looking for a particular group/object.
 *
 * If multiple groups/objects are quickly hidden in sequence (say user hides object 1, then a second later object 2 and so on)
 * - merge them into a batch to reduce marking objects as frozen too often,
 * because every time set of frozen objects changes - either scene or nodes are reloaded.
 *
 * NOTE: (un)freezing objects causes scene reload. Try to avoid it as much as possible.
 */
export function useAutofreeze() {
    const autofreezeEnabled = useAppSelector(selectAutofreezeEnabled);
    useAutofreezeGroups(autofreezeEnabled);
    useAutofreezeDefaultVisibility(autofreezeEnabled);
    useAutofreezeHidden(autofreezeEnabled);
}

function useAutofreezeGroups(autofreezeEnabled: boolean) {
    const groups = useObjectGroups();
    const prevGroupsToFreeze = useRef<string[]>([]);
    const dispatchObjectGroups = useDispatchObjectGroups();
    const freezingQueueRef = useRef(new FreezingQueue<string>(GROUP_FREEZING_DELAY, GROUP_QUEUE_MERGE_INTERVAL));

    useEffect(() => {
        if (!autofreezeEnabled) {
            freezingQueueRef.current.clear();
            return;
        }

        const groupsToFreeze = groups
            .filter((group) => group.status === GroupStatus.Hidden && !group.promotedToFrozen)
            .map((g) => g.id);
        const toAdd = groupsToFreeze.filter((id) => !prevGroupsToFreeze.current.includes(id));
        const toRemove = prevGroupsToFreeze.current.filter((id) => !groupsToFreeze.includes(id));

        freezingQueueRef.current.update({
            toAdd,
            toRemove,
            onTimeout: (items) => {
                for (const groupId of items) {
                    dispatchObjectGroups(objectGroupsActions.update(groupId, { promotedToFrozen: true }));
                }
            },
        });

        prevGroupsToFreeze.current = groupsToFreeze;
    }, [autofreezeEnabled, groups, dispatchObjectGroups]);
}

function useAutofreezeDefaultVisibility(autofreezeEnabled: boolean) {
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!autofreezeEnabled) {
            return;
        }

        if (defaultVisibility !== ObjectVisibility.Transparent) {
            dispatch(renderActions.setUseFilterForTransparency(false));
            return;
        }

        const timer = setTimeout(() => {
            dispatch(renderActions.setUseFilterForTransparency(true));
        }, DEFAULT_ACTION_FREEZING_DELAY);

        return () => clearTimeout(timer);
    }, [autofreezeEnabled, dispatch, defaultVisibility]);
}

function useAutofreezeHidden(autofreezeEnabled: boolean) {
    const hidden = useHidden();
    const dispatchHidden = useDispatchHidden();

    const prevHidden = useRef<Pick<typeof hidden, "ids" | "idArr">>({
        ids: SelectionSet.EMPTY,
        idArr: SelectionSet.EMPTY.sortedIds,
    });

    const freezingQueueRef = useRef(new FreezingQueue<number>(HIDDEN_FREEZING_DELAY, HIDDEN_QUEUE_MERGE_INTERVAL));

    useEffect(() => {
        if (!autofreezeEnabled) {
            freezingQueueRef.current.clear();
            return;
        }

        // Newly hidden ids
        const toAdd =
            prevHidden.current.idArr.length > 0
                ? hidden.idArr.filter((id) => !prevHidden.current.ids.has(id))
                : hidden.idArr;
        // Newly unhidden ids
        const toRemove =
            hidden.idArr.length > 0
                ? prevHidden.current.idArr.filter((id) => !hidden.ids.has(id))
                : prevHidden.current.idArr;

        freezingQueueRef.current.update({
            toAdd,
            toRemove,
            onTimeout: (items) => {
                dispatchHidden(hiddenActions.addFrozen(items));
            },
        });

        prevHidden.current = {
            ids: hidden.ids,
            idArr: hidden.idArr,
        };
    }, [autofreezeEnabled, dispatchHidden, hidden.ids, hidden.idArr]);
}

type QueueItem<T> = {
    items: Set<T>;
    timeout: NodeJS.Timeout;
    createdAt: number;
};

/**
 * Example with hidden object IDs (same happens with groups):
 * - Hide [1,2,3] - add to freezing queue
 * - Unhide [2] - remove from freezing queue
 * - Hide [2,4] - add to freezing queue (queue is [[1,3], [2,4]])
 * - Hide [5] within merge interval - merge with last queue item (queue is [[1,3], [2,4,5]])
 */
class FreezingQueue<T> {
    queue: QueueItem<T>[] = [];

    /**
     * @param delay after which to freeze items
     * @param mergeInterval if another item is queued within this interval from the last queued item -
     * items are merged into one queue item. Old timer is cleared and new timer is set.
     * This is to prevent too many scene reloads when user quickly hides/unhides items,
     * while still not making all the objects waiting forever if the user keeps hiding/unhiding stuff.
     */
    constructor(
        public readonly delay: number,
        public readonly mergeInterval: number,
    ) {}

    update({
        toAdd,
        toRemove,
        onTimeout,
    }: {
        toAdd: Iterable<T>;
        toRemove: Iterable<T>;
        onTimeout: (items: Iterable<T>) => void;
    }) {
        // Remove toRemove IDs from freezing queue
        outer: for (const id of toRemove) {
            // Going top-down because there's a higher chance that recently
            // hidden items get unhidden
            for (let i = this.queue.length - 1; i >= 0; i--) {
                const item = this.queue[i];
                if (item.items.delete(id)) {
                    if (item.items.size === 0) {
                        clearTimeout(item.timeout);
                        this.queue.splice(i, 1);
                    }
                    continue outer;
                }
            }
        }

        // Add toAdd IDs to freezing queue
        const additions = new Set<T>(toAdd);
        if (additions.size > 0) {
            const createdAt = Date.now();
            const prevQueueItem = this.queue.at(-1);
            // Merge with previous queue item if it was created less than mergeInterval ago
            // to prevent updating "filter" highlight too often (which causes flickering)
            const mergeWithPreviousQueueItem =
                prevQueueItem && createdAt - prevQueueItem.createdAt < this.mergeInterval;
            const items = additions;
            if (mergeWithPreviousQueueItem) {
                for (const item of prevQueueItem!.items) {
                    items.add(item);
                }
            }
            const timeout = setTimeout(() => {
                const qIndex = this.queue.findIndex((q) => q.items === items);
                if (qIndex !== -1) {
                    this.queue.splice(qIndex, 1);
                }
                onTimeout(items);
            }, this.delay);

            if (mergeWithPreviousQueueItem) {
                clearTimeout(prevQueueItem!.timeout);
                this.queue.pop();
            }

            this.queue.push({ items, timeout, createdAt });
        }
    }

    clear() {
        for (const item of this.queue) {
            clearTimeout(item.timeout);
        }
        this.queue = [];
    }
}
